import React, { useEffect } from "react";
import { SiteHeader, SiteFooter } from "./SiteChrome";
import { CONTACT, TRIAL_DAYS } from "./siteConfig";
import "./Site.css";

const LAST_UPDATED = "29 October 2026";

// TODO before publishing: replace these with the real legal details, and have
// the page reviewed by a lawyer. The bracketed values render as-is.
const LEGAL_ENTITY = "Hexasphere Technologies";
const REGISTERED_ADDRESS = "B-402, Mahindra Royale, Pimpri, Pune - 410018";
const GOVERNING_LAW = "the laws of India";
const COURTS = "Pune, India";

export default function SiteTermsPage() {
  useEffect(() => {
    document.title = "Terms of Service — DecisionFlow";
  }, []);

  /* The app shell locks html/body to a non-scrolling 100% viewport and paints
     its own background. Released here for as long as this page is mounted. */
  useEffect(() => {
    document.documentElement.classList.add("site-html");
    document.body.classList.add("site-body");
    return () => {
      document.documentElement.classList.remove("site-html");
      document.body.classList.remove("site-body");
    };
  }, []);

  return (
    <div className="site">
      <SiteHeader />

      <main id="top">
        <section className="site-section">
          <div className="site-container site-legal">
            <p className="site-eyebrow">Legal</p>
            <h1>Terms of Service</h1>
            <p className="site-legal-updated">Last updated {LAST_UPDATED}</p>

            <p>
              These terms cover your company's use of DecisionFlow — the
              Project &amp; Document Management and Manufacturing &amp;
              Production Tracking applications — provided by {LEGAL_ENTITY}{" "}
              ("Hexasphere", "we", "us"), {REGISTERED_ADDRESS}. By creating a
              workspace or using the product, the company you act for ("you")
              agrees to them. If you have signed an order form or a separate
              agreement with us, that document wins where the two differ.
            </p>

            <h2>Your workspace and users</h2>
            <ul>
              <li>Each company gets its own workspace. Its admins decide who has an account and what each role can see and do.</li>
              <li>You are responsible for the people you give accounts to, for keeping their logins private, and for what is done under them. Tell us promptly if you think an account has been misused.</li>
              <li>Accounts are for named individuals. Don't share a login between people.</li>
            </ul>

            <h2>Free trial</h2>
            <p>
              New workspaces may start with a {TRIAL_DAYS}-day trial. A trial
              is provided as-is, for evaluation, and ends at the end of its
              period unless you move to a paid plan. We may end or change a
              trial at any time.
            </p>

            <h2>Plans and payment</h2>
            <ul>
              <li>Prices are quoted by our team and set out in your quote or order form. Fees are in Indian rupees unless agreed otherwise, and exclude applicable taxes, which we add to the invoice.</li>
              <li>Payments are processed by our payment provider. You pay in advance for the plan period you choose.</li>
              <li>If a payment is not made by its due date, access to the workspace is paused until it is. Your data is kept during that time.</li>
              <li>Refunds are not automatic. If you think one is due, contact us; we decide case by case and, where we agree, refund through the original payment method.</li>
            </ul>

            <h2>Your data</h2>
            <p>
              The projects, documents, orders and other content your company
              puts into DecisionFlow belong to your company. You give us
              permission to store and process it only to run the product for
              you, as our <a href="/privacy">Privacy Policy</a> describes. You
              are responsible for having the right to put that content into
              the product, including any personal data about your staff or
              clients.
            </p>

            <h2>Acceptable use</h2>
            <p>Don't use DecisionFlow to:</p>
            <ul>
              <li>break the law or infringe anyone else's rights;</li>
              <li>upload malware or anything meant to disrupt the service;</li>
              <li>try to reach another company's workspace or data, or get around access controls;</li>
              <li>overload the service, scrape it, or resell access without our written agreement;</li>
              <li>send unsolicited messages through the WhatsApp or Telegram features.</li>
            </ul>
            <p>We may suspend accounts that do, and will tell you why.</p>

            <h2>Integrations and AI assistants</h2>
            <p>
              Optional integrations — WhatsApp, Telegram, and the read-only
              connector for AI assistants such as Claude, ChatGPT and Codex
              — are switched on by your users or admins. When they are, data
              passes to that third party, whose own terms apply to it; see{" "}
              <a href="/ai-assistants">how the AI connector works</a>. We are
              not responsible for third-party services or for what an AI
              assistant writes from the data it reads, so check its answers
              before relying on them.
            </p>

            <h2>Self-hosted deployments</h2>
            <p>
              If DecisionFlow runs on infrastructure you control, the
              separate agreement for that deployment governs hosting,
              updates, backups and support, and these terms apply to
              everything it does not cover.
            </p>

            <h2>Availability and support</h2>
            <p>
              We work to keep DecisionFlow available and to fix problems
              quickly, but we don't promise uninterrupted service, and
              planned maintenance may occasionally pause it. Support is by
              email and phone, as listed on our{" "}
              <a href="/support">support page</a>. We may improve or change
              features over time; we won't remove a core feature you rely on
              without telling admins first.
            </p>

            <h2>Our liability</h2>
            <p>
              DecisionFlow is provided "as is". To the extent the law allows,
              we are not liable for indirect or consequential losses — such
              as lost profits, revenue or data — and our total liability for
              any claim is limited to the fees you paid us in the 12 months
              before it arose. Nothing in these terms limits liability that
              cannot be limited by law.
            </p>

            <h2>Ending the service</h2>
            <ul>
              <li>You can stop using DecisionFlow at the end of any paid period by telling us. Fees already paid are not refunded except as described above.</li>
              <li>We may end or suspend a workspace that breaches these terms, after notice where reasonable.</li>
              <li>After a workspace closes, you can ask us to export its data within 30 days. After that we delete it, as our Privacy Policy describes, except records we must keep by law.</li>
            </ul>

            <h2>Changes to these terms</h2>
            <p>
              We may update these terms. If a change materially affects you,
              we'll update the date above and tell account admins before it
              takes effect. Continuing to use DecisionFlow after that means
              you accept the new terms.
            </p>

            <h2>Governing law</h2>
            <p>
              These terms are governed by {GOVERNING_LAW}, and the courts of{" "}
              {COURTS} have exclusive jurisdiction over any dispute about them.
            </p>

            <h2>Contact</h2>
            <p>
              Questions about these terms:{" "}
              <a href={`mailto:${CONTACT.supportEmail}`}>{CONTACT.supportEmail}</a> or{" "}
              <a href={`tel:${CONTACT.phoneHref}`}>{CONTACT.phone}</a>.
            </p>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
